import React from "react";

import { StyledIconWrapper } from "./components/styled/StyledIconWrapper";
import { ICON_SETS } from "./constants";
import { IconProps, IconSize, IconType } from "./interfaces";

/**
 * Icon component renders an SVG icon based on a name string.
 *
 * @component
 * @param {Object} props - Component props
 * @param {string} props.name - The icon identifier in the format `iconName_<size>_<type>`,
 *   e.g., `arrow_12_fill` or `edit_18_outline`. Size must be 12 or 18; type must be `fill` or `outline`.
 * @param {string} [props.color="currentColor"] - Optional Design Token (COLOR_TOKENS) color value to apply to the icon.
 * @returns {JSX.Element | null} Returns the SVG icon wrapped in a styled container, or `null` if
 *   no matching icon is found. Developers should ensure the `name` prop matches an exported
 *   key in the corresponding icon module.
 *
 * @example
 * // Render a filled 12px arrow icon in red:
 * <Icon name="arrow_12_fill" color={COLOR_TOKENS.status.success[11]} />
 * //If any icon is missing I needs to be added in the assets folder and then imported in the index.tsx file
 */
const Icon: React.FC<IconProps> = React.memo(
  ({ name, color = "currentColor" }) => {
    // Split the name string to derive type and size
    const parts = name.split("_");
    const type = parts[parts.length - 1].toLowerCase() as IconType;
    const size = parseInt(parts[parts.length - 2], 10) as IconSize;

    // Lookup the module for the specified type and size
    const IconModule = ICON_SETS[type]?.[size];
    // Select the specific SVG component by its export name
    const SpecificIcon = IconModule?.[name];

    if (!SpecificIcon) {
      console.warn(`Icon not found: ${name}`);
      return null;
    }

    return (
      <StyledIconWrapper $color={color}>
        <SpecificIcon />
      </StyledIconWrapper>
    );
  },
);

Icon.displayName = "Icon";

export default Icon;
